import { styled } from "goober";
import { useSyncExternalStore } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import { PressButton } from "./PressButton";
import { Tooltip } from "./Tooltip";

let sidebarTogglePreviewHovered = false;
let sidebarTogglePreviewSuppressed = false;
const sidebarTogglePreviewListeners = new Set<() => void>();
function notifySidebarTogglePreview() {
  for (const listener of sidebarTogglePreviewListeners) listener();
}
function sidebarTogglePreviewActive() {
  return sidebarTogglePreviewHovered && !sidebarTogglePreviewSuppressed;
}
function setSidebarTogglePreviewHovered(value: boolean) {
  if (sidebarTogglePreviewHovered === value) return;
  sidebarTogglePreviewHovered = value;
  notifySidebarTogglePreview();
}
function suppressSidebarTogglePreview() {
  if (sidebarTogglePreviewSuppressed) return;
  sidebarTogglePreviewSuppressed = true;
  notifySidebarTogglePreview();
}
function clearSidebarTogglePreviewSuppression() {
  if (!sidebarTogglePreviewSuppressed) return;
  sidebarTogglePreviewSuppressed = false;
  notifySidebarTogglePreview();
}
function useSidebarTogglePreviewActive() {
  return useSyncExternalStore(
    (callback) => {
      sidebarTogglePreviewListeners.add(callback);
      return () => sidebarTogglePreviewListeners.delete(callback);
    },
    sidebarTogglePreviewActive,
    () => false
  );
}
function useSidebarTogglePreviewSuppressed() {
  return useSyncExternalStore(
    (callback) => {
      sidebarTogglePreviewListeners.add(callback);
      return () => sidebarTogglePreviewListeners.delete(callback);
    },
    () => sidebarTogglePreviewSuppressed,
    () => false
  );
}

const SidebarToggleButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:active:not(:disabled) {
    background: var(--alpha8);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  & svg {
    opacity: 0.25;
  }
  & .inner {
    transition: width 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-collapsed="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="false"][data-preview="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="true"][data-preview="true"] .inner {
    width: 4px;
  }
`;

export function SidebarToggle() {
  const isMobile = useIsMobile();
  const isNarrow = useIsNarrow();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();
  const sidebarTogglePreview = useSidebarTogglePreviewActive();
  const sidebarTogglePreviewClickSuppressed =
    useSidebarTogglePreviewSuppressed();
  const label = sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar";

  if (isMobile || isNarrow) return null;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Tooltip
        hidden={sidebarTogglePreviewClickSuppressed}
        content={
          <>
            {label}{" "}
            <span style={{ fontSize: 10, color: "var(--text-secondary)" }}>
              ⌘\
            </span>
          </>
        }
      >
        <SidebarToggleButton
          aria-label={label}
          data-collapsed={sidebarCollapsed}
          data-preview={sidebarTogglePreview}
          onMouseEnter={() => setSidebarTogglePreviewHovered(true)}
          onMouseLeave={() => {
            setSidebarTogglePreviewHovered(false);
            clearSidebarTogglePreviewSuppression();
          }}
          onPress={() => {
            suppressSidebarTogglePreview();
            setSidebarCollapsed(!sidebarCollapsed);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M11 2C12.6569 2 14 3.34315 14 5L14 11C14 12.6569 12.6569 14 11 14L6 14L5 14C3.34315 14 2 12.6569 2 11L2 5C2 3.34315 3.34315 2 5 2L6 2L11 2Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect
              className="inner"
              x="4"
              y="4"
              width="4"
              height="8"
              rx="1.5"
              fill="currentColor"
            />
          </svg>
        </SidebarToggleButton>
      </Tooltip>
    </div>
  );
}
